import { Injectable } from '@angular/core';

export enum TOKEN_FEATURE {
    CONTRACT_CHAT = 'CONTRACT_CHAT',
    COMPARE_CHAT = 'COMPARE_CHAT',
    COMPARE = 'COMPARE',
    GENERATE_WITH_AI = 'GENERATE_WITH_AI',
    TAG_EXTRACTION = 'TAG_EXTRACTION',
    CHECKLIST = 'CHECKLIST',
    AI_TOOLING = 'AI_TOOLING',
    SUMMARY = 'SUMMARY',
    GEOTECHNICAL_SUMMARY = 'GEOTECHNICAL_SUMMARY',
    GEOTECHNICAL_ANALYSIS_SUMMARY = 'GEOTECHNICAL_ANALYSIS_SUMMARY',
    GEOTECHNICAL_CHAT = 'GEOTECHNICAL_CHAT',
    MISCELLANEOUS = 'MISCELLANEOUS',
    DUPLICATE_QUESTIONS_DETECTION = 'DUPLICATE_QUESTIONS_DETECTION',
    GENERAL_AGENT = 'GENERAL_AGENT',
    QNA_CHAT = 'QNA_CHAT',
    GENERAL_AGENT_ASK = 'GENERAL_AGENT_ASK',
    /** AI extraction of questions from the form document. */
    QUESTION_EXTRACTION = 'QUESTION_EXTRACTION',
    /** AI generation of answers for extracted questions. */
    QUESTION_ANSWERING = 'QUESTION_ANSWERING',
}
import { HttpClient } from '@angular/common/http';
import { BaseService } from 'src/app/@core/services/base.service';
import { environment } from 'src/environments/environment.dev';


export interface UserCreditEntry {
    allocated: number;
    consumed: number;
    remaining: number;
    resets: number | null;
    expires: string | null;
}

/**
 * Per-agent/feature sub-row shown when a usage group is expanded.
 * Mirrors the `UsageChild` interface on the Node backend.
 */
export interface UsageChild {
    /** Human-readable agent/feature label (e.g. "Report Summary", "AI Dashboard"). */
    feature: string;
    /** Distinct credit types for this feature, sorted ASK then AGENT. */
    modes: string[];
    /** Summed consumedCredits per credit type (e.g. { ASK: 0.20, AGENT: 0.80 }). */
    creditsByType: Record<string, number>;
    /** Summed token count for this feature. */
    totalTokens: number;
    /**
     * ISO-8601 timestamp of the most recent raw record for this feature within the group.
     * Shown in the Date column of level-2 rows as a time (e.g. "3:45 PM").
     */
    lastRunAt?: string;
}

/**
 * One date-wise usage group as returned by the backend.
 * Records are pre-grouped server-side by entity (thread / analysis / document) × billing date.
 * Mirrors the `UsageGroup` interface on the Node backend.
 */
export interface UsageGroup {
    /** Stable key composed of groupingId + billing date, used for row tracking. */
    key: string;
    /** Display title: thread title, analysis name, contract name, etc. */
    title: string;
    /** Billing date as YYYY-MM-DD string. */
    dateStr: string;
    /** Distinct credit types across the group, sorted ASK then AGENT. */
    modes: string[];
    /** Summed credits per credit type for the whole group. */
    creditsByType: Record<string, number>;
    /** Summed token count across the group. */
    totalTokens: number;
    /** Per-agent/feature breakdown — shown when the parent row is expanded. */
    children: UsageChild[];
}

export interface UserCreditsData {
    ask: UserCreditEntry;
    agent: UserCreditEntry;
    /** Usage records for the current page, pre-grouped by entity × billing date. */
    usage: UsageGroup[];
    /** Total number of groups across all pages (after applying any type filter). */
    totalGroups: number;
    /** Current page number (1-indexed). */
    page: number;
    /** Groups per page. */
    limit: number;
    /** Total number of pages. */
    totalPages: number;
    /** Credits-exhaustion history. */
    exhaustions: {
        feature: string;
        featureId: string;
        threadId?: string;
        reason: string;
        error?: string;
        agentName: string;
    }[];
}

export interface CreditPricing {
    askCreditRatio: number;
    agentCreditRatio: number;
}

export interface CreditAlertUser {
    user: { _id: string; fullName?: string; firstName?: string; lastName?: string; email?: string };
    type: 'ASK' | 'AGENT';
    allocated: number;
    consumed: number;
    available: number;
    pct: number;
}

export interface OrgAnalyticsData {
    creditSummary: { _id: 'ASK' | 'AGENT'; totalAllocated: number; totalConsumed: number; totalAvailable: number; userCount: number }[];
    seats: { total: number; withCredits: number };
    limitAlerts: { atLimit: CreditAlertUser[]; blocked: CreditAlertUser[]; negativeBalance: CreditAlertUser[] };
}

export interface UsageTrendPoint {
    _id: { year: number; month: number; day: number };
    totalCredits: number;
    totalTokens: number;
    askCredits: number;
    agentCredits: number;
}

@Injectable({ providedIn: 'root' })
export class UserCreditsService extends BaseService {
    constructor(protected override http: HttpClient) {
        super(http);
    }

    private apiUrl = environment.apiUrl + '/user-credits';

    /**
     * Fetches the current user's credits and paginated usage groups.
     * @param opts.page  1-indexed page number (default 1).
     * @param opts.limit Groups per page (default 20).
     * @param opts.type  Optional credit-type filter: "ask" | "agent" | "all".
     */
    getCurrentCredits(opts: { page?: number; limit?: number; type?: string } = {}) {
        const params = Object.fromEntries(
            Object.entries(opts).filter(([, v]) => v !== undefined && v !== 'all')
        ) as Record<string, string | number>;
        return this.get<UserCreditsData>(
            `${this.apiUrl}/current`,
            Object.keys(params).length ? params : undefined,
        );
    }

    /**
     * Fetches paginated usage groups for a specific user (admin use).
     * @param opts.page  1-indexed page number (default 1).
     * @param opts.limit Groups per page (default 20).
     * @param opts.type  Optional credit-type filter: "ask" | "agent" | "all".
     */
    getCreditsByUser(userId: string, opts: { page?: number; limit?: number; type?: string } = {}) {
        return this.post<UserCreditsData>(`${this.apiUrl}/by-user`, { userId, ...opts });
    }

    getCurrentPricing() {
        return this.get<CreditPricing>(`${this.apiUrl}/current-pricing`);
    }

    checkCreditsAllocationStatus(sessionId: string) {
        return this.post<any>(`${this.apiUrl}/check-credits-allocation-status`, { sessionId });
    }

    getOrgAnalytics(params: { userId?: string; type?: string; model?: string; tier?: string } = {}) {
        const filtered = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
        return this.get<OrgAnalyticsData>(`${this.apiUrl}/org-analytics`, Object.keys(filtered).length ? filtered : undefined);
    }

    getUsageTrend(params: { startDate?: string; endDate?: string; userId?: string; type?: string } = {}) {
        const filtered = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
        return this.get<UsageTrendPoint[]>(`${this.apiUrl}/usage-trend`, Object.keys(filtered).length ? filtered : undefined);
    }

    resumeThread(threadId: string, feature: string, featureId: string, agentName: string) {
        return this.post<{ threadId: string }>(`${environment.apiUrl}/thread/resume`, { threadId, feature, featureId, agentName });
    }

    /** Remove a specific credits-exhaustion record for the current user. */
    removeExhaustion(feature: string, featureId: string) {
        return this.post(`${this.apiUrl}/remove-exhaustion`, { feature, featureId });
    }
}
